import { parseISO, startOfMonth } from 'date-fns';
import { toNativeDate, toOptionalDateString } from 'utils/date';

import type { Dayjs } from 'dayjs';

import type {
  OpeningBlockResponse,
  OpeningScheduleRequest,
  OpeningScheduleResponse
} from 'generated/index';

import { parseTimeStringToDayjs } from 'components/shared/ControlledFields/ControlledTimePicker.utils';

import type {
  OpeningBlockFormValues,
  OpeningScheduleFormValues
} from '../OpeningHours.types';

/**
 * Where an empty time field's picker opens: the usual opening of a day care, rather than the time of
 * day. The field itself stays empty until a time is picked.
 */
export const DEFAULT_START_TIME = '07:00';
export const DEFAULT_END_TIME = '18:00';

export const toTime = (time?: string | null): Dayjs | null =>
  parseTimeStringToDayjs(time);

export const fromTime = (time: Dayjs | null): string | undefined =>
  time && time.isValid() ? time.format('HH:mm') : undefined;

export const emptyBlock = (): OpeningBlockFormValues => ({
  monday: false,
  tuesday: false,
  wednesday: false,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
  startTime: null,
  endTime: null,
  description: '',
  closed: false,
  dayTypes: ['REGULAR']
});

const toBlockFormValues = (
  block: OpeningBlockResponse
): OpeningBlockFormValues => ({
  monday: block.monday,
  tuesday: block.tuesday,
  wednesday: block.wednesday,
  thursday: block.thursday,
  friday: block.friday,
  saturday: block.saturday,
  sunday: block.sunday,
  startTime: toTime(block.startTime),
  endTime: toTime(block.endTime),
  description: block.description ?? '',
  closed: block.closed,
  dayTypes: [...block.dayTypes]
});

export const toFormValues = (
  schedule?: OpeningScheduleResponse
): OpeningScheduleFormValues => {
  if (!schedule) {
    return {
      name: '',
      description: '',
      validFrom: startOfMonth(new Date()),
      validTo: null,
      blocks: [emptyBlock()]
    };
  }
  return {
    name: schedule.name,
    description: schedule.description ?? '',
    validFrom: parseISO(schedule.validFrom),
    validTo: schedule.validTo ? parseISO(schedule.validTo) : null,
    blocks: schedule.blocks.map(toBlockFormValues)
  };
};

const emptyToUndefined = (value: string) =>
  value.trim() === '' ? undefined : value.trim();

/** A closed line carries no times, whatever the fields still hold. */
export const toRequest = (
  values: OpeningScheduleFormValues
): OpeningScheduleRequest => ({
  name: values.name.trim(),
  description: emptyToUndefined(values.description),
  validFrom: toOptionalDateString(toNativeDate(values.validFrom)),
  validTo: toOptionalDateString(toNativeDate(values.validTo)) || undefined,
  blocks: values.blocks.map((block) => ({
    monday: block.monday,
    tuesday: block.tuesday,
    wednesday: block.wednesday,
    thursday: block.thursday,
    friday: block.friday,
    saturday: block.saturday,
    sunday: block.sunday,
    startTime: block.closed ? undefined : fromTime(block.startTime),
    endTime: block.closed ? undefined : fromTime(block.endTime),
    description: emptyToUndefined(block.description),
    closed: block.closed,
    dayTypes: block.dayTypes
  }))
});
